// Стопка панелей из адреса (?p=вид:id,вид:id — кодек shared/routing/search).
// Правило 4: панель адресуема — ссылку коллега открывает с той же стопкой. До трёх
// уровней, Esc снимает верхнюю. Содержимое кладут разделы через реестр панелей; вид, которого
// в реестре нет, каркас честно называет и объясняет, а не показывает белый прямоугольник.

import { useCallback, useEffect, useRef, useState } from 'react';

import { RiCloseLine } from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import { buildSearch, parseSearch, popPanel } from '@/shared/routing/search';
import { useСреда } from '@/shared/среда';
import { cn } from '@/utils/cn';

import { panelDef } from './panel-registry';

/** Сдвиг нижних панелей влево: края стопки видны, глубина читается без счётчика. */
// Сдвиг стопки: верхняя панель без него, каждая следующая левее — так видно, что их несколько.
//
// В узком окне сдвига НЕТ: панель там во всю ширину, и сдвинутый на 16 точек край читается не
// как глубина, а как съехавшая вёрстка. Глубину там показывает шеврон с названием нижней панели.
const DEPTH_OFFSET: readonly string[] = ['', 'узко:translate-x-0 -translate-x-4', 'узко:translate-x-0 -translate-x-8'];

/**
 * Закрыть панель смахиванием вправо.
 *
 * ВТОРЫМ способом, а не единственным: крестик в шапке остаётся на месте и остаётся главным
 * (`ПРАВИЛА.md`: у экрана обязан быть ВИДИМЫЙ выход). Смахивание добавляется потому, что на
 * полноширинной панели крестик уезжает в верхний угол, а большой палец живёт внизу.
 *
 * Условие «движение горизонтальное» обязательно: панель прокручивают вертикально, и без
 * проверки любое пролистывание формы уводило бы её с экрана.
 */
function useСмахивание(закрыть: () => void, включено: boolean) {
  const начало = useRef<{ x: number; y: number } | null>(null);
  const [сдвиг, setСдвиг] = useState(0);

  if (!включено && сдвиг !== 0) {
    setСдвиг(0);
  }

  const наНачало = (событие: React.PointerEvent<HTMLElement>) => {
    if (!включено || событие.pointerType !== 'touch') {
      return;
    }
    начало.current = { x: событие.clientX, y: событие.clientY };
  };

  const наДвижение = (событие: React.PointerEvent<HTMLElement>) => {
    const точка = начало.current;
    if (точка === null) {
      return;
    }
    const поX = событие.clientX - точка.x;
    const поY = событие.clientY - точка.y;
    // Вертикальное движение — это прокрутка содержимого, панель за ним не едет.
    if (Math.abs(поY) > Math.abs(поX)) {
      начало.current = null;
      setСдвиг(0);
      return;
    }
    setСдвиг(Math.max(0, поX));
  };

  const наКонец = (событие: React.PointerEvent<HTMLElement>) => {
    const точка = начало.current;
    начало.current = null;
    if (точка === null) {
      return;
    }
    const пройдено = событие.clientX - точка.x;
    setСдвиг(0);
    // Треть ширины панели — порог, на котором намерение уже видно, а случайное касание ещё нет.
    const ширина = событие.currentTarget.getBoundingClientRect().width;
    if (пройдено > ширина / 3) {
      закрыть();
    }
  };

  return {
    сдвиг,
    события: {
      onPointerDown: наНачало,
      onPointerMove: наДвижение,
      onPointerUp: наКонец,
      onPointerCancel: наКонец,
    },
  };
}

export function PanelStack() {
  const location = useLocation();
  const navigate = useNavigate();

  const state = parseSearch(location.search);
  const { panels } = state;
  const среда = useСреда();

  // Закрытие верхней панели собирается один раз на адрес: эффект Esc ссылается на него
  // напрямую, а не пересказывает своими словами, из чего оно состоит.
  const closeTop = useCallback(() => {
    const next = popPanel(parseSearch(location.search));
    navigate(`${location.pathname}${buildSearch(next)}`);
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (panels.length === 0) {
      return;
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) {
        return;
      }
      event.preventDefault();
      closeTop();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeTop, panels.length]);

  // Смахивание живёт только там, где панель занимает экран целиком: в трёхколоночном окне она
  // стоит рядом с содержимым, и «увести её вбок» пальцем означало бы задеть работу под ней.
  const включено = среда.ввод === 'тач' && среда.ширина === 'узко';
  const смахнуть = useСмахивание(closeTop, включено);

  if (panels.length === 0) {
    return null;
  }

  return (
    // Панель на бумагу не идёт: она лежит ПОВЕРХ документа, и печать окна вместе с ней
    // означала бы лист, наполовину закрытый мастером выдачи.
    <div data-печать='скрыть' className='pointer-events-none absolute inset-0 z-40'>
      {/* Затемнение под панелью.
          
          До 04.09.2026 панель просто ложилась поверх колонки детали и обрезала её посередине:
          кнопка «Оформить возврат» уходила под панель наполовину. Тень у карточек в теме
          направлена ВНИЗ (`0 16px 32px -12px`), и на левом краю панели во всю высоту её не
          видно вовсе — экран читался как поломка вёрстки, а не как «поверх открыто окно».
          
          Закрывает по нажатию, как шторка в приложении: там это записано прямо — «потянуть
          вниз, нажать по затемнению, нажать крестик». Два поведения одного продукта на двух
          устройствах расходиться не должны. */}
      {/* Скрыто от чтения с экрана намеренно: закрыть панель голосом и с клавиатуры можно
          крестиком в её шапке и клавишей Esc, а вторая кнопка «Закрыть панель» в дереве
          доступности — это два одинаковых пункта подряд, между которыми нечего выбирать. */}
      <div
        aria-hidden='true'
        data-затемнение=''
        onClick={closeTop}
        className='pointer-events-auto absolute inset-0 bg-overlay animate-in fade-in duration-200 ease-out motion-reduce:animate-none'
      />
      {panels.map((panel, index) => {
        const isTop = index === panels.length - 1;
        const смахивание = включено && isTop ? смахнуть : null;
        // Глубина от верха стопки: верхняя без сдвига, каждая следующая ниже — левее.
        const depth = panels.length - 1 - index;
        const def = panelDef(panel.view);
        return (
          <section
            key={`${panel.view}:${panel.id}`}
            aria-label={def === undefined ? `Панель «${panel.view}»` : def.title(panel.id)}
            {...(смахивание?.события ?? {})}
            style={
              смахивание !== null && смахивание.сдвиг > 0
                ? { transform: `translateX(${смахивание.сдвиг}px)`, transition: 'none' }
                : undefined
            }
            className={cn(
              // Край панели — линия ПОСИЛЬНЕЕ обычной обводки карточки.
              //
              // Тень темы направлена вниз (`0 16px 32px -12px`), и на левом краю панели во всю
              // высоту её не видно вовсе. Затемнение решает это в светлой теме, а в тёмной
              // веер AlignUI светлый и на тёмном фоне почти не читается — там весь край
              // держит именно эта линия.
              'pointer-events-auto absolute inset-y-0 right-0 flex w-panel flex-col border-l border-stroke-sub-300 bg-bg-white-0 shadow-regular-md transition-transform duration-200 ease-out',
              // Ландшафт планшета: панель шире, иначе от детали под ней остаётся 261 точка.
              // Портрет: во всю ширину — делить 744 точки между списком и панелью нечем.
              'средне:w-panel-wide узко:w-full узко:border-l-0',
              // Панель ВЫЕЗЖАЕТ справа, а не возникает на месте. Появившийся из ниоткуда
              // прямоугольник в полэкрана человек читает как «что-то сломалось и перерисовалось»;
              // движение из-за края отвечает на вопрос, откуда он взялся и куда закроется.
              // Двести миллисекунд — та же ступень, что у остальных переходов продукта.
              'animate-in slide-in-from-right-4 duration-200 ease-out motion-reduce:animate-none',
              // Полноширинная панель, выезжающая на 16 точек, выглядит как подёргивание, а не
              // как движение из-за края: заметить начало движения не на чем. В узком она едет
              // на всю свою ширину.
              'узко:slide-in-from-right',
              DEPTH_OFFSET[depth],
            )}
          >
            <header className='flex items-center gap-2 border-b border-stroke-soft-200 px-4 py-3'>
              <div className='flex min-w-0 flex-1 flex-col'>
                <h2 className='truncate text-label-sm text-text-strong-950'>
                  {def === undefined ? `Панель «${panel.view}»` : def.title(panel.id)}
                </h2>
                {def === undefined && (
                  <span className='truncate text-paragraph-xs text-text-sub-600'>{panel.id}</span>
                )}
              </div>
              {isTop && (
                <button
                  type='button'
                  onClick={closeTop}
                  aria-label='Закрыть панель (Esc)'
                  className='цель-тача relative flex size-8 shrink-0 items-center justify-center rounded-lg text-text-soft-400 transition duration-200 ease-out hover:bg-bg-weak-50 hover:text-text-strong-950'
                >
                  <RiCloseLine className='size-5' />
                </button>
              )}
            </header>
            {def === undefined ? (
              <div className='flex min-h-0 flex-1 items-center justify-center overflow-y-auto p-4'>
                <p className='max-w-xs text-center text-paragraph-sm text-text-sub-600'>
                  Содержимое панели «{panel.view}» появится вместе с экранами своего раздела. Адрес
                  уже настоящий: эта ссылка откроет ту же стопку.
                </p>
              </div>
            ) : (
              // Поле панели живёт ЗДЕСЬ, а не в каждой панели по отдельности. До 25.08.2026
              // его клали сами панели: семнадцать клали `p-4`, а пять денежных — выдачу,
              // возврат, передачу, заявку и отчёт по чеку — не клали ничего, и поля формы
              // упирались прямо в край выдвижной панели. Это было самое заметное глазу место
              // во всём кабинете, и найти его по коду было нечем: каждая панель по
              // отдельности выглядела правильной.
              //
              // Значение 16, как у шапки панели выше и как у колонки списка: панель шириной
              // 384 точки — это узкая колонка, а не деталь (`.claude/rules/ui-views.md`,
              // «Свой отступ»).
              <div className='min-h-0 flex-1 overflow-y-auto p-4'>
                {def.render({ id: panel.id, onClose: closeTop })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
